// @flow

import { connect } from 'react-redux';
import type { State as Experiences } from 'app/common/redux/reducers/experiences';
import { createSelector } from 'reselect';
import { isRenderableExperience } from 'app/common/lib/react/experiences/experienceUtils';
import type {
  Experience,
  ExperienceMap,
  PlacementMountMap,
} from 'app/common/lib/react/experiences/experienceUtils';
import {
  experienceFetch,
  experienceFetchAll,
  experienceComplete,
  experienceDismiss,
  experienceView,
  placementExperimentsTrigger,
} from 'app/common/redux/actions/experiences';
import { bindActionCreators, type Dispatch } from 'redux';

type DispatchProps = {
  completeExperience: typeof experienceComplete,
  dismissExperience: typeof experienceDismiss,
  fetchExperienceForPlacement: typeof experienceFetch,
  fetchAllExperiences: typeof experienceFetchAll,
  viewExperience: typeof experienceView,
  triggerExperimentsForPlacement: typeof placementExperimentsTrigger,
};
type StateProps = {
  experienceForPlacement: ?Experience,
};
export type ExperienceClient = DispatchProps & StateProps;
export type ExperienceClientProps = { experienceClient: ExperienceClient };

const eligibleExperiencesSelector = (state) => state.experiences.eligibleExperiences;
const mountedPlacementsSelector = (state) => state.experiences.mountedPlacements;
const placementIdSelector = (state, props) => props.placementId;
export const getExperienceForPlacement = (
  eligibleExperiences: ExperienceMap,
  mountedPlacements: PlacementMountMap,
  placementId: number,
) => {
  const experience = eligibleExperiences[placementId];
  if (mountedPlacements[placementId] && isRenderableExperience(experience)) {
    return experience;
  }
  return null;
};

const getExperienceForPlacementSelector: (
  state: { experiences: Experiences },
  props: { placementId: number },
) => ?Experience = createSelector(
  eligibleExperiencesSelector,
  mountedPlacementsSelector,
  placementIdSelector,
  getExperienceForPlacement,
);

function mapStateToProps(
  state: { experiences: Experiences },
  props: { placementId: number },
): StateProps {
  return { experienceForPlacement: getExperienceForPlacementSelector(state, props) };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
  return bindActionCreators(
    {
      completeExperience: experienceComplete,
      dismissExperience: experienceDismiss,
      fetchAllExperiences: experienceFetchAll,
      fetchExperienceForPlacement: experienceFetch,
      triggerExperimentsForPlacement: placementExperimentsTrigger,
      viewExperience: experienceView,
    },
    dispatch,
  );
}

function mergeProps(
  stateProps: StateProps,
  dispatchProps: DispatchProps,
  ownProps: Object,
): ExperienceClientProps {
  return {
    experienceClient: { ...stateProps, ...dispatchProps },
    ...ownProps,
  };
}

// $FlowFixMe: add connect<Props, OwnProps, , , _, _>() typing to remove this error
export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
);
